import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Drawer, Button, Form, Select, Input, Space, Badge } from 'antd'
import { DateRangePicker } from '../../input-date-range/styles'
import { InputInteger, InputPhone, InputRange } from './elements'
import InputText from '../../input-text'
import dayjs from 'dayjs'
import { FilterOutlined } from '@ant-design/icons'
import { currentFiltersProps } from '../../../pages_v2/deliveries/list/interface'
import { filtersType } from '../filters/interfaces'

interface FiltersDrawerProps {
  filters: filtersType[]
  onChange: (values: Record<string, any>) => void
  currentFilters: currentFiltersProps
}

interface IFiltersData {
  [name: string]: any
}

interface Option {
  label?: string | number
  value?: string | number
}

export interface FiltersDrawerRef {
  toggle: () => void
}

const FiltersDrawer = forwardRef<FiltersDrawerRef, FiltersDrawerProps>(
  ({ filters, onChange, currentFilters }, ref) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [formKey, setFormKey] = useState(0)
    const [form] = Form.useForm()
    const drawerFilterNames = useMemo(() => {
      const names = new Set<string>()
      filters.forEach(filter => {
        if (filter.type === 'filter_group') {
          filter.items?.forEach(item => {
            if (item.name) {
              names.add(item.name)
            }
          })
          return
        }
        if (filter.name) {
          names.add(filter.name)
        }
      })
      return names
    }, [filters])

    const filterNamesWithRules = useMemo(() => {
      const names: string[] = []
      filters.forEach(filter => {
        if (filter.type === 'filter_group') {
          filter.items?.forEach(item => {
            if (item.name && item.options?.rules?.length) {
              names.push(item.name)
            }
          })
          return
        }
        if (filter.name && filter.options?.rules?.length) {
          names.push(filter.name)
        }
      })
      return names
    }, [filters])
    const [activeFilters, setActiveFilters] = useState<IFiltersData>(
      currentFilters
        ? Object.fromEntries(
            Object.entries(currentFilters).filter(
              ([, value]) => value !== undefined && value !== null,
            ),
          )
        : {},
    )

    useImperativeHandle(ref, () => ({
      toggle: () => setIsDrawerOpen(prev => !prev),
    }))

    useEffect(() => {
      const newActiveFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(
          ([, value]) =>
            value !== undefined &&
            value !== null &&
            (typeof value !== 'object' || Object.keys(value).length > 0),
        ),
      )
      setActiveFilters(newActiveFilters)

      const fieldsForForm: Record<string, any> = {}

      const applyFilterValue = (filter: filtersType) => {
        if (!filter.name) {
          return
        }
        if (filter.type === 'dates') {
          const dateValue = currentFilters?.[filter.name]
          if (typeof dateValue === 'string' && dateValue.includes('-')) {
            const [start, end] = dateValue.split('-')
            fieldsForForm[filter.name] = [dayjs(start, 'YYYY.MM.DD'), dayjs(end, 'YYYY.MM.DD')]
          } else {
            fieldsForForm[filter.name] = undefined
          }
          return
        }
        if (filter.type === 'select_multiple') {
          const filterOptions = filter.options?.enum || []
          const filterValue = currentFilters?.[filter.name]
          const values = Array.isArray(filterValue)
            ? filterValue
            : typeof filterValue === 'string'
            ? filterValue.split(',').filter(Boolean)
            : []
          fieldsForForm[filter.name] = values.map(value => {
            const option = filterOptions.find(
              opt => opt.value !== undefined && String(opt.value) === String(value),
            )
            return option ? option.value : value
          })
          return
        }
        if (filter.type === 'select') {
          const filterOptions = filter.options?.enum || []
          const filterValue = currentFilters?.[filter.name]
          const option = filterOptions.find(
            opt => opt.value !== undefined && String(opt.value) === String(filterValue),
          )
          fieldsForForm[filter.name] = option ? option.value : filterValue
          return
        }
        if (filter.type === 'range') {
          fieldsForForm[filter.name] = currentFilters?.[filter.name] || {
            min: undefined,
            max: undefined,
          }
          return
        }
        fieldsForForm[filter.name] = currentFilters?.[filter.name]
      }

      filters.forEach(filter => {
        if (filter.type === 'filter_group' && filter.items?.length) {
          filter.items.forEach(item => applyFilterValue(item))
          return
        }
        applyFilterValue(filter)
      })

      form.setFieldsValue(fieldsForForm)
    }, [currentFilters, filters, form])

    const handleClearFilters = () => {
      const newActiveFilters = Object.fromEntries(
        Object.entries(activeFilters)
          .filter(([key]) => filters.some(f => f.name === key && f.skipReset))
          .map(([key, value]) => [key, value]),
      )

      form.resetFields()
      setFormKey(prev => prev + 1)
      setActiveFilters(newActiveFilters)
      onChange(newActiveFilters)
    }

    const handleApplyFilters = useCallback(() => {
      const applyValues = (values: Record<string, any>) => {
        const transformedValues: IFiltersData = {}

        const findFilterByName = (key: string): filtersType | undefined => {
          const top = filters.find(f => f.name === key)
          if (top) return top
          for (const f of filters) {
            if (f.type === 'filter_group' && f.items) {
              const item = f.items.find(i => i.name === key)
              if (item) return item
            }
          }
          return undefined
        }

        Object.keys(values).forEach(key => {
          const filter = findFilterByName(key)
          if (!filter) return

          if (filter.type === 'dates' && Array.isArray(values[key]) && values[key][0]?.format) {
            const [start, end] = values[key]
            transformedValues[key] = `${start.format('YYYY.MM.DD')}-${end.format('YYYY.MM.DD')}`
          } else if (filter.type === 'range' && values[key]) {
            if (values[key].min !== undefined) {
              transformedValues[`${key}[min]`] = values[key].min
            }
            if (values[key].max !== undefined) {
              transformedValues[`${key}[max]`] = values[key].max
            }
          } else if (filter.type === 'select_multiple' && Array.isArray(values[key])) {
            transformedValues[key] = values[key].join(',')
          } else {
            transformedValues[key] = values[key]
          }
        })

        const removeEmptyKeys = (data: IFiltersData) => {
          Object.keys(data).forEach(key => {
            if (
              data[key] === null ||
              data[key] === undefined ||
              data[key] === '' ||
              (typeof data[key] === 'object' &&
                data[key] !== null &&
                !Object.keys(data[key]).length)
            ) {
              delete data[key]
            }
          })
          return data
        }

        const mergedValues = currentFilters
          ? {
              ...Object.fromEntries(
                Object.entries(currentFilters).filter(([key]) => !drawerFilterNames.has(key)),
              ),
              ...transformedValues,
            }
          : transformedValues
        const activeFilters = removeEmptyKeys(mergedValues)
        setActiveFilters(activeFilters)
        onChange(activeFilters)
        setIsDrawerOpen(false)
      }

      if (filterNamesWithRules.length > 0) {
        form
          .validateFields(filterNamesWithRules)
          .then(() => applyValues(form.getFieldsValue()))
          .catch(() => {
            // Валидация не прошла — drawer остаётся открытым
          })
      } else {
        applyValues(form.getFieldsValue())
      }
    }, [
      filters,
      form,
      filterNamesWithRules,
      setActiveFilters,
      onChange,
      currentFilters,
      drawerFilterNames,
    ])

    const selectFilterOption = (input: string, option?: Option): boolean => {
      const inputLower = input.toLowerCase()
      const label = option?.label ? String(option.label).toLowerCase() : ''
      const value = option?.value ? String(option.value).toLowerCase() : ''
      return label.includes(inputLower) || value.includes(inputLower)
    }

    const getInput = (filter: filtersType) => {
      const { type, options, placeholder, name, disabled, value, prefix } = filter

      switch (type) {
        case 'filter_group':
          break
        case 'input-string':
          if (options?.displayFormat && options?.mask) {
            return (
              <InputText
                schema={{
                  type: 'string',
                  options: { displayFormat: options.displayFormat, mask: options.mask },
                }}
              />
            )
          }
          return (
            <Input
              placeholder={placeholder as string}
              style={{ width: '100%' }}
              value={value}
              onChange={e => form.setFieldsValue({ [name]: e.target.value || undefined })}
              onPressEnter={handleApplyFilters}
              allowClear={true}
            />
          )
        case 'input-integer':
          return (
            <InputInteger
              prefix={prefix}
              defaultValue={value as number}
              onChange={v => form.setFieldsValue({ [name]: v })}
              onPressEnter={handleApplyFilters}
              style={{ width: '100%' }}
              placeholder={placeholder as string}
            />
          )
        case 'phone':
          return (
            <InputPhone
              defaultValue={value as string}
              onChange={v => form.setFieldsValue({ [name]: v || undefined })}
            />
          )
        case 'select':
          return (
            <Select
              allowClear={options?.allowClear !== undefined ? options.allowClear : true}
              placeholder={placeholder || 'Выберите'}
              options={options?.enum || []}
              disabled={disabled || false}
              style={{ width: '100%' }}
              showSearch
              filterOption={selectFilterOption}
              onChange={v => form.setFieldsValue({ [name]: v || undefined })}
              defaultValue={value}
            />
          )
        case 'select_multiple':
          return (
            <Select
              allowClear={options?.allowClear !== undefined ? options.allowClear : true}
              placeholder={placeholder || 'Выберите'}
              options={options?.enum || []}
              disabled={disabled || false}
              style={{ width: '100%' }}
              onChange={v => form.setFieldsValue({ [name]: v || undefined })}
              mode="multiple"
              filterOption={selectFilterOption}
            />
          )
        case 'dates':
          return <DateRangePicker style={{ width: '100%' }} format="YYYY.MM.DD" />
        case 'range':
          return (
            <InputRange
              value={value as { min?: number; max?: number }}
              onChange={v => form.setFieldsValue({ [name]: v })}
              min={options?.min}
              max={options?.max}
              placeholderMin={placeholder ? `${placeholder} (от)` : 'От'}
              placeholderMax={placeholder ? `${placeholder} (до)` : 'До'}
            />
          )
        default:
          return null
      }
    }

    const renderFilters = filters.map(filter => {
      if (filter.type === 'filter_group' && filter.items?.length) {
        return (
          <Form.Item key={filter.name} label={filter.label} style={{ width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filter.items.map((item, idx) => (
                <Form.Item
                  key={item.name || idx}
                  name={item.name}
                  style={{ width: '100%', marginBottom: 0, ...item.style }}
                  rules={item.options?.rules}
                >
                  {getInput(item)}
                </Form.Item>
              ))}
            </div>
          </Form.Item>
        )
      }

      return (
        <Form.Item
          key={filter.name}
          name={filter.name}
          label={filter.label}
          style={{ width: '100%' }}
          rules={filter.options?.rules}
        >
          {getInput(filter)}
        </Form.Item>
      )
    })

    return (
      <>
        <Space style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            ghost={Object.keys(activeFilters).length === 0}
            onClick={() => setIsDrawerOpen(true)}
          >
            Фильтры
            <Badge
              count={Object.keys(activeFilters).length}
              size={'small'}
              style={{ position: 'absolute', left: 6 }}
              offset={[-20, -20]}
            />
          </Button>
          <Button onClick={handleClearFilters} disabled={Object.keys(activeFilters).length === 0}>
            Сбросить фильтры
          </Button>
        </Space>
        <Drawer
          title="Фильтры"
          placement="right"
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          width={400}
          footer={
            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
              <Button type="primary" onClick={handleApplyFilters}>
                Применить
              </Button>
              <Button onClick={handleClearFilters}>Сбросить</Button>
            </div>
          }
          zIndex={1010}
        >
          <Form
            key={formKey}
            form={form}
            layout="vertical"
            initialValues={filters.reduce((acc, cur) => {
              if (
                cur.type === 'dates' &&
                typeof cur.value === 'string' &&
                cur.value.includes('-')
              ) {
                const [start, end] = cur.value.split('-')
                acc[cur.name] = [dayjs(start, 'YYYY.MM.DD'), dayjs(end, 'YYYY.MM.DD')]
              } else {
                acc[cur.name] = cur.value
              }
              return acc
            }, {} as Record<string, any>)}
          >
            {renderFilters}
          </Form>
        </Drawer>
      </>
    )
  },
)

FiltersDrawer.displayName = 'FiltersDrawer'
export default FiltersDrawer
